import { ReactNode } from 'react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { LocationProvider } from './LocationContext';
import { LimitedLocationProvider } from './LimitedLocationContext';

interface ProviderWrapperProps {
  children: ReactNode;
}

/**
 * ProviderWrapper - Conditionally renders the appropriate location provider
 * 
 * This wrapper checks the current route and renders either:
 * - LimitedLocationProvider for /limited routes (PIN sessions and email employees)
 * - LocationProvider for all other routes (full admin/corporate access)
 * 
 * This prevents provider conflicts and ensures only one provider initializes per route.
 */
export const ProviderWrapper = ({ children }: ProviderWrapperProps) => {
  const location = useRouterLocation();
  
  // Check if we're on a /limited route
  const isLimitedRoute = location.pathname === '/limited' ||
                         location.pathname.startsWith('/limited/');

  // Super admin route doesn't need location context (platform-level, not company-level)
  const isSuperAdminRoute = location.pathname === '/super-admin' ||
                            location.pathname.startsWith('/super-admin/');

  console.log('[PROVIDER_WRAPPER] Route check:', {
    pathname: location.pathname,
    isLimitedRoute,
    isSuperAdminRoute,
    provider: isSuperAdminRoute ? 'none' : isLimitedRoute ? 'LimitedLocationProvider' : 'LocationProvider'
  });

  // Super admin doesn't need any location provider
  if (isSuperAdminRoute) {
    return <>{children}</>;
  }

  // Render appropriate provider based on route
  if (isLimitedRoute) {
    return <LimitedLocationProvider>{children}</LimitedLocationProvider>;
  }

  return <LocationProvider>{children}</LocationProvider>;
};
